import { useCallback, useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router';
import { atom, useAtom } from 'jotai';
import type { ChatRecipient, Conversation, TextOrFileMessage } from '../types';
import { getSelectedConversationID, INBOX_URL } from '../utils/url';
import { useChatBoxes } from './use-chat-boxes';

interface NewMessageState {
    /**
     * The recipients of the new message. This is exclusive with `selectedConversation`.
     */
    recipients: ChatRecipient[];
    /**
     * The currently selected conversation in the new message. This is exclusive with `recipients`.
     */
    selectedConversation: Conversation | null;
    /**
     * Member IDs to resolve into recipients once contact suggestions are available.
     */
    pendingRecipientIDs: number[];
}

/**
 * The new message state. Shared between all chat widgets (embedded or floating).
 */
const newMessageStateAtom = atom<NewMessageState>({
    recipients: [],
    selectedConversation: null,
    pendingRecipientIDs: [],
});

/**
 * The pending message to be sent when the user sends a message in a new message
 * chat widget. When the chat with this conversation is opened, the message will
 * be sent automatically.
 */
const pendingMessageAtom = atom<{
    /**
     * The conversation ID to send the pending message to.
     */
    conversationID: number;
    /**
     * The professional ID if sending from a company inbox context.
     */
    professionalID: number | null;
    message: string;
    attachments: File[];
    replyTo?: TextOrFileMessage;
} | null>(null);

/**
 * The message being forwarded. When set, the new message chat widget (conversationID=0)
 * will be in "forward mode", showing a preview of the forwarded message and
 * disabling file attachments. The forwarded message ID will be sent as the
 * `replyToID` when creating a new message.
 */
const forwardedMessageAtom = atom<TextOrFileMessage | null>(null);

/**
 * Shared chat state used by floating chat boxes and embedded chat inbox.
 */
export function useChatGlobals({
    global = false,
}: {
    /**
     * Only one instance of this hook should set this to `true` (in the root
     * component) to run the `useEffect` hooks.
     */
    global?: boolean;
} = {}) {
    const { pathname } = useLocation();
    const [searchParams] = useSearchParams();
    const { isNewMessageChatBoxOpen } = useChatBoxes();
    const [newMessageState, setNewMessageState] = useAtom(newMessageStateAtom);
    const [pendingMessage, setPendingMessage] = useAtom(pendingMessageAtom);
    const [forwardedMessage, setForwardedMessage] =
        useAtom(forwardedMessageAtom);
    const isOnInboxPage =
        pathname === INBOX_URL || pathname.startsWith(`${INBOX_URL}/`);
    const isOnNewMessagePage =
        isOnInboxPage && getSelectedConversationID(searchParams) === 0;

    // Clear new message state and forwarded message when there is nowhere to compose a new message.
    useEffect(() => {
        if (!global) return;
        if (isOnInboxPage) {
            if (isOnNewMessagePage) return;
        } else if (isNewMessageChatBoxOpen) {
            return;
        }
        setNewMessageState((prev) => ({
            ...prev,
            recipients: [],
            selectedConversation: null,
        }));
        setForwardedMessage(null);
    }, [
        global,
        isOnInboxPage,
        isOnNewMessagePage,
        isNewMessageChatBoxOpen,
        setNewMessageState,
        setForwardedMessage,
    ]);

    return {
        /**
         * Shared state for composing a new message in floating chat boxes.
         */
        newMessageState,
        /**
         * The pending message to be sent when the chat widget for this
         * conversation is opened.
         */
        pendingMessage,
        /**
         * The message being forwarded. When set, the new message chat widget
         * will be in "forward mode".
         */
        forwardedMessage,
        /**
         * Set the recipients in the new message chat box.
         */
        setNewMessageRecipients: useCallback(
            (recipients: ChatRecipient[]) => {
                setNewMessageState({
                    recipients,
                    selectedConversation: null,
                    pendingRecipientIDs: [],
                });
            },
            [setNewMessageState],
        ),
        /**
         * Set the selected conversation in the new message chat box.
         */
        setNewMessageConversation: useCallback(
            (selectedConversation: Conversation | null) => {
                setNewMessageState({
                    recipients: [],
                    selectedConversation,
                    pendingRecipientIDs: [],
                });
            },
            [setNewMessageState],
        ),
        /**
         * Queue member IDs to add as recipients once contact suggestions are
         * available. Use when opening the new message chat box from outside.
         */
        setNewMessagePendingRecipientIDs: useCallback(
            (pendingRecipientIDs: number[]) => {
                setNewMessageState({
                    recipients: [],
                    selectedConversation: null,
                    pendingRecipientIDs,
                });
            },
            [setNewMessageState],
        ),
        /**
         * Set the pending message to be sent when the conversation is opened.
         */
        setPendingMessage,
        /**
         * Set the message to forward. Pass `null` to clear the forwarding state.
         */
        setForwardedMessage,
    };
}
